import { useState } from 'react';
import { Link } from 'react-router';
import { clsx } from 'clsx';

const steps = [
  {
    num: '01',
    title: 'Check Your Eligibility',
    subtitle: 'Free initial check',
    description: "Answer a few quick questions about your incident. Mac checks whether you may have the basis for a claim and identifies the authority likely to be responsible — your local council, National Highways, or another authority.",
    details: [
      'When and where did the damage occur?',
      'What type of damage to your vehicle?',
      'Do you have photos and repair costs?',
    ],
    outcome: 'You receive a general indication against common criteria, not legal advice or a prediction of the outcome.',
    icon: '✓',
  },
  {
    num: '02',
    title: 'Gather Your Evidence',
    subtitle: 'Guided upload',
    description: 'Upload your photos and documents. Mac flags missing or unclear information for you to review.',
    details: [
      'Photos of the pothole (ideally with measurement)',
      'Photos of the damage to your vehicle',
      'Repair invoice or quote from a garage',
      'Location pinpointed on a map',
    ],
    outcome: 'Mac can check public prior-report sources. Any result is contextual information for you to review.',
    icon: '📁',
  },
  {
    num: '03',
    title: 'Get Your Claim Pack',
    subtitle: '£35, available now',
    description: 'Once you finish providing evidence, Mac prepares a claim letter and organised evidence bundle for you to review.',
    details: [
      'Structured claim letter based on the information you provide',
      'Organised evidence bundle',
      'Step-by-step submission guide for your specific council',
      'Know Your Rights briefing — general information on council duties',
    ],
    outcome: 'You review and submit the pack yourself. You stay in control.',
    icon: '📄',
  },
  {
    num: '04',
    title: 'Submit & Manage Your Claim',
    subtitle: 'You handle submission and correspondence',
    description: "You send your claim to the authority using Fixtyer's guide and keep your own submission record. Fixtyer may provide general templates and process information.",
    details: [
      'Check the authority’s published response process',
      'General follow-up templates if you need them',
      'You decide how to respond to offers or rejections',
    ],
    outcome: 'You manage all correspondence, deadlines, decisions and any negotiation.',
    icon: '📤',
  },
  {
    num: '05',
    title: 'Escalate If Rejected',
    subtitle: 'Court escalation guidance',
    description: 'If the authority rejects your claim, you can review its response and official information about possible next steps. Dedicated court document preparation is still being developed.',
    details: [
      'Guidance for reviewing the rejection',
      'Escalation checklist',
      'Links to official small claims court resources',
      'Court document preparation planned for a later release',
    ],
    outcome: 'Court action involves fees, deadlines and risk. Review official guidance and consider independent legal advice.',
    icon: '⚖️',
  },
];

export function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const currentStep = steps[activeStep];

  return (
    <section id="how-it-works" className="py-24 bg-brand-950 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12">
          <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
            THE PROCESS
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mt-3 mb-4">
            How It Works
          </h2>
          <p className="text-brand-300 text-lg max-w-2xl">
            An overview of document preparation and your own claim submission.
          </p>
        </div>

        {/* Steps Layout */}
        <div className="grid lg:grid-cols-[320px_1fr] gap-8">
          {/* Step Navigation */}
          <div className="flex flex-col">
            {steps.map((step, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={clsx(
                  'relative p-5 text-left border border-brand-800 transition-all duration-200',
                  'hover:border-accent-500/50 hover:bg-accent-500/5',
                  activeStep === i && 'border-accent-500 bg-accent-500/10'
                )}
              >
                {/* Active indicator */}
                {activeStep === i && (
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent-500" />
                )}
                
                <div className="flex items-center gap-4">
                  <span className={clsx(
                    'font-mono text-sm font-semibold',
                    activeStep === i ? 'text-accent-500' : 'text-brand-400'
                  )}>
                    {step.num}
                  </span>
                  <div>
                    <div className={clsx(
                      'font-semibold',
                      activeStep === i ? 'text-white' : 'text-brand-300'
                    )}>
                      {step.title}
                    </div>
                    <div className={clsx(
                      'text-sm mt-0.5',
                      activeStep === i ? 'text-accent-500' : 'text-brand-400'
                    )}>
                      {step.subtitle}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Step Detail */}
          <div className="p-8 lg:p-10 border border-brand-800 bg-gradient-to-br from-accent-500/5 to-transparent">
            {currentStep && (
              <div>
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={clsx(
                    'w-14 h-14 bg-accent-500/10 border border-accent-500/30 flex items-center justify-center text-2xl',
                    currentStep.num === '01' && 'text-orange-500'
                  )}>
                    {currentStep.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-semibold text-white">
                      {currentStep.title}
                    </h3>
                    <span className="text-accent-500 text-sm font-medium">
                      {currentStep.subtitle}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-brand-200 text-lg leading-relaxed mb-8">
                  {currentStep.description}
                </p>

                {/* What's involved */}
                <div className="bg-black/30 p-6 mb-8">
                  <div className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">
                    What's involved
                  </div>
                  <ul className="space-y-3">
                    {currentStep.details.map((detail, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-accent-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-brand-100">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Outcome */}
                <div className="flex items-start gap-3 p-5 bg-accent-500/10 border-l-[3px] border-accent-500">
                  <span className="text-lg">💡</span>
                  <p className="text-brand-100 leading-relaxed">
                    {currentStep.outcome}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile step indicator */}
        <div className="flex justify-center gap-1 mt-8 lg:hidden" role="tablist" aria-label="Process steps">
          {steps.map((step, i) => (
            <button
              key={i}
              onClick={() => setActiveStep(i)}
              className="w-11 h-11 flex items-center justify-center"
              aria-label={`Step ${i + 1}: ${step.title}`}
              aria-selected={activeStep === i}
              role="tab"
            >
              <span className={clsx(
                'w-2.5 h-2.5 rounded-full transition-colors',
                activeStep === i ? 'bg-accent-500' : 'bg-brand-700'
              )} />
            </button>
          ))}
        </div>

        {/* Learn more CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 text-accent-400 hover:text-accent-300 font-medium transition-colors"
          >
            Learn more about the process
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
